import React, { useMemo } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { BREAKPOINT } from '@constants';
import { ProductData, getShopProducts } from '@data/storefront';
import { useCountry } from '@hooks/useCountry';
import { applyCountrySpecificSorting, filterProducts } from '@utils/product';
import Carousel from '../Carousel';
import ProductCard from './ProductCard';
import { TextVariant, Typography } from './Typography';

export interface Props {
  productCodes: string[];
  title?: string | null;
  isCarousel?: boolean;
}

const flickityOptions = {
  initialIndex: 0,
  groupCells: 1,
  prevNextButtons: false,
  contain: true,
  cellAlign: 'left',
  watchCSS: false,
  draggable: true
};

const Upsell: React.FC<Props> = ({ productCodes = [], title, isCarousel = true }) => {
  const { t } = useTranslation();
  const { country } = useCountry();
  const shopProducts = getShopProducts();
  const products = applyCountrySpecificSorting(country, filterProducts(country, shopProducts));
  const filteredProducts: ProductData[] = [];

  productCodes.forEach((code) => {
    const foundProduct = products.find((product) => product.product_code_override?.text === code || product.product_code === code);

    if (foundProduct !== undefined) {
      filteredProducts.push(foundProduct);
    }
  });

  return (
    <Container>
      {title && filteredProducts.length ? (
        <Title>
          <Typography $variant={TextVariant.UpperText4} $mobileVariant={TextVariant.UpperText5}>
            {t(title)}
          </Typography>
        </Title>
      ) : null}
      <ProductCardsWrap>
        {useMemo(
          () => (
            <>
              {isCarousel ? (
                <Carousel options={flickityOptions} key={filteredProducts.map((p) => p.product_code).join('-')}>
                  {filteredProducts.map((productData, index) => (
                    <Slide key={productData?.product_code_override?.text || productData?.product_code} $last={index === filteredProducts.length - 1}>
                      <ProductCard productData={productData} hideDescription noUppercaseTitle />
                    </Slide>
                  ))}
                </Carousel>
              ) : (
                filteredProducts.map((productData) => (
                  <FixedProductContainer key={productData?.product_code_override?.text || productData?.product_code}>
                    <ProductCard productData={productData} hideDescription noUppercaseTitle />
                  </FixedProductContainer>
                ))
              )}
            </>
          ),
          [filteredProducts]
        )}
      </ProductCardsWrap>
    </Container>
  );
};

export default Upsell;

const Container = styled.div`
  width: 100%;
`;

const Slide = styled.div<{ $last: boolean }>`
  width: auto;
  margin-right: ${(props) => (props.$last ? '0' : '16px')};

  @media (max-width: ${BREAKPOINT.tablet}px) {
    margin-right: ${(props) => (props.$last ? '0' : '12px')};
  }
`;

const Title = styled.div`
  margin-bottom: 16px;
  text-align: left;

  @media (max-width: ${BREAKPOINT.tablet}px) {
    margin-bottom: 20px;
  }
`;

const ProductCardsWrap = styled.div`
  position: relative;
  width: 100%;

  .flickity-viewport {
    overflow: visible;
  }

  .carousel:after {
    content: 'flickity';
    display: none; /* hide :after */
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    /* disable Flickity for large devices */
    .carousel:after {
      content: '';
    }
  }
`;

const FixedProductContainer = styled.div`
  margin-bottom: 32px;
`;
